"use client"

import type React from "react"

import { useEffect, useState } from "react"
import useTitle from "../../shared/hooks/useTitle"
import Modal from "../../shared/components/Modal"
import MODAL_NAMES from "../dialogs/ModalName"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../shared/functions/Context"
import { LoadingEllipsis } from "../../shared/components/loading/Loading"
import { useNavigate, useParams } from "react-router-dom"
import useBackButton from "../../shared/hooks/useBack"
import type { IReviewCycleStatus, IReviewCycleType } from "../../shared/models/ScorecardBatch"
import useIndividualScorecard from "../../shared/hooks/useIndividualScorecard"
import ErrorBoundary from "../../shared/components/error-boundary/ErrorBoundary"

import EmployeeQ2ReviewCycleResolveDispute from "./EmployeeQ2ReviewCycleResolveDispute"
import SubmitScorecardDisputeModal from "../dialogs/submit-dispute/SubmitMidtermDisputeModal"
import SubmitMidtermDisputeResolutionModal from "../dialogs/submit-dispute/SubmitMidtermDisputeResolutionModal"
import SubmitFinalAssessmentDisputeResolutionModal from "../dialogs/submit-dispute/SubmitFinalAssessmentDisputeResolutionModal"
import EmployeeQ4ReviewCycleResolveDispute from "./EmployeeQ4ReviewCycleResolveDispute"



const ResolveEmployeeDisputeScorecardView = observer(() => {
  const { store, api, ui } = useAppContext()
  const { uid } = useParams()

  const [loading, setLoading] = useState(false)
  const [_, setTitle] = useTitle("") // set page title

  const [activeTab, setActiveTab] = useState<"midterm" | "final">("midterm")

  const objectives = store.objective.getByUid(uid!)
  const measures = store.measure.getByUid(uid!)

  const scorecard = store.scorecard.active
  const selectedUser = store.user.selected
  const currentUser = store.auth.meJson

  const agreement = useIndividualScorecard(uid)

  useBackButton("/c/scorecards/dispute/")
  const navigate = useNavigate()

  useEffect(() => {
    const load = async () => {
      if (!uid) return
      setLoading(true)
      await api.objective.getAll(uid)
      await api.measure.getAll(uid)
      setLoading(false)
    }
    load()
  }, [api.measure, api.objective, uid])

  return (
    <ErrorBoundary>
      {!loading && (
        <ErrorBoundary>
          <div className="w-full">
            <div className="bg-gradient-to-r from-[#3f6a8e] to-[#84cbe9] px-8 py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">Resolve Disputes</h1>
                  <p className="text-blue-100 mt-1">Review and resolve employee scorecard disputes</p>
                </div>
              </div>
            </div>

            <div className="px-8 py-6">
              <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg max-w-md">
                <button
                  type="button"
                  onClick={() => setActiveTab("midterm")}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    activeTab === "midterm" ? "bg-white text-[#3f6a8e] shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Midterm Disputes
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("final")}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    activeTab === "final" ? "bg-white text-[#3f6a8e] shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Final Assessment Disputes
                </button>
              </div>

              {activeTab === "midterm" && (
                <EmployeeQ2ReviewCycleResolveDispute
                  agreement={agreement}
                  selectedUser={selectedUser!}
                  currentUser={currentUser!}
                  measures={measures}
                  objectives={objectives}
                />
              )}

              {activeTab === "final" && (
                <EmployeeQ4ReviewCycleResolveDispute
                  agreement={agreement}
                  selectedUser={selectedUser!}
                  currentUser={currentUser!}
                  measures={measures}
                  objectives={objectives}
                />
              )}
            </div>
          </div>
        </ErrorBoundary>
      )}
      {/* MEASURE_READ_ONLY_MODAL */}
      {loading && <LoadingEllipsis />}

      <Modal modalId={MODAL_NAMES.EXECUTION.SCORECARD_DISPUTE_Q2_MODAL}>
        <SubmitScorecardDisputeModal />
      </Modal>
      <Modal modalId={MODAL_NAMES.EXECUTION.SCORECARD_RESOLVE_DISPUTE_Q2_MODAL}>
        <SubmitMidtermDisputeResolutionModal />
      </Modal>
      <Modal modalId={MODAL_NAMES.EXECUTION.SCORECARD_RESOLVE_DISPUTE_Q4_MODAL}>
        <SubmitFinalAssessmentDisputeResolutionModal />
      </Modal>
    </ErrorBoundary>
  )
})

export default ResolveEmployeeDisputeScorecardView
